import Layout from 'HorizontalMenu/Menu'
import Navbar from 'HorizontalMenu/Navbar'
import React, { useEffect, useState } from 'react'
import { Card, CardBody, CardHeader, Col, Container, Label, Nav, NavItem, NavLink, Row, TabContent, TabPane } from 'reactstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery } from 'react-query'
import Select from 'react-select'
import classnames from 'classnames'
import { Skeleton, Stack } from '@mui/material'
import Message from 'Components/Message'
import ErrorComponent from 'Components/ErrorComponent'
import FormLoading from 'Components/FormLoading'
import { RootState } from 'index'
import { customStyle } from 'shared/CommonCSS'
import { queryKeyes } from 'shared/queryKeys'
import { getVesselsAction, setOnboardedVessels, setVesselID } from 'Store/Generic/ReportingSlice'
import { OnboardingStatus, Roles, VesselTypeConstant } from 'shared/constants'
import { Get } from 'VesselMaster/vesselMaster.hooks'
import { getDefaultVesselValue } from 'GenericForms/Helper'
import { userVessels, hasRole } from 'utils/auth/authUtils'
import FileTable from 'VesselMaster/FileTable'
import { useDocumentTitle } from 'Components/useDocument.hooks'

const AllUploadedFiles = () => {
    useDocumentTitle("View Uploaded Files - ecoSAIL");
    const dispatch = useDispatch()
    const [selectedYear, setSelectedYear] = useState(null)
    const [activeAdjustmentsTab, setActiveAdjustmentsTab] = useState("1")
    const [title, setTitle] = useState<string>('BDN Files')
    const [description, setDescription] = useState<string>('Uploaded BDN Files')

    const { Vessels, VesselID, onboardedVessels } = useSelector(
        (state: RootState) => state.Reporting
    )

    const vessel = Vessels.find((rec) => rec.id === VesselID)
    const applicableVessels = userVessels()
    const vesselType = Vessels.find((item) => item.id === VesselID)?.vessel_type_precedence_id

    /* -------------------- Vessel Selection -------------------- */
    const handleVesselSelectionChanges = (e: any) => {
        dispatch(setVesselID(e ? e.id : null))
        setSelectedYear(null)
        setActiveAdjustmentsTab("1")
    }

    /* -------------------- Tab Toggle -------------------- */
    const toggle = (tab: string) => {
        if (activeAdjustmentsTab !== tab) {
            setActiveAdjustmentsTab(tab)
        }
    }

    /* -------------------- Query: Vessel Years -------------------- */
    const { data: vesselYears, isLoading: vesselYearsLoading } = useQuery(
        [queryKeyes.vessel.VesselYear.key, VesselID],
        () => Get(queryKeyes.vessel.VesselYear.url(VesselID), VesselID),
        { enabled: !!VesselID }
    )

    /* -------------------- Query: Other Settings -------------------- */
    const { data: OtherSettings } = useQuery(
        [queryKeyes.vessel.VesselOtherSettingsObject.key, VesselID],
        () => Get(queryKeyes.vessel.VesselOtherSettingsObject.url(VesselID), VesselID),
        {
            enabled: !!VesselID,
            staleTime: Infinity,
        }
    )

    /* -------------------- Query: All Files -------------------- */
    const { data: AllFiles, isLoading, isError } = useQuery(
        [queryKeyes.vessel.ViewFilesByVessel.key, VesselID, selectedYear],
        () => Get(queryKeyes.vessel.ViewFilesByVessel.url(VesselID, selectedYear)),
        {
            enabled: !!VesselID && !!selectedYear,
            staleTime: Infinity,
        }
    )

    /* -------------------- Load Vessels -------------------- */
    useEffect(() => {
        dispatch(getVesselsAction('vessel_master'))
    }, [dispatch])

    /* -------------------- Set Onboarded Vessels -------------------- */
    useEffect(() => {
        if (Array.isArray(Vessels)) {
            dispatch(
                setOnboardedVessels(
                    Vessels.filter(
                        (v) => v.onboarding_status === OnboardingStatus.ONBOARDED
                    )
                )
            )
        }
    }, [Vessels, dispatch])

    return (
        <React.Fragment>
            <Layout>
                <Navbar />
                <div className="page-content">
                    <Container fluid>
                        <h4 className="page_title pos-start">All Uploaded Files</h4>
                        <Card className='p-3 mb-3'>
                            <Row>
                                {/* Vessel */}
                                <Col md={4}>
                                    <Label>Select Vessel</Label>
                                    <Select
                                        options={
                                            onboardedVessels.filter(
                                                (item) =>
                                                    item.display === true &&
                                                    applicableVessels?.includes(item.id)
                                            ) as any[]
                                        }
                                        getOptionLabel={(o: any) => o.vessel_name_type_name}
                                        getOptionValue={(o: any) => o.id}
                                        isClearable
                                        defaultValue={getDefaultVesselValue(
                                            VesselID,
                                            vessel,
                                            onboardedVessels,
                                            applicableVessels
                                        )}
                                        onChange={handleVesselSelectionChanges}
                                        menuPortalTarget={document.body}
                                        styles={customStyle}
                                        isLoading={Vessels.length === 0}
                                    />
                                </Col>

                                {/* Year */}
                                <Col md={2}>
                                    <Label>Select Year</Label>
                                    <Select
                                        options={vesselYears || []}
                                        getOptionLabel={(o: any) => `${o.year}`}
                                        getOptionValue={(o: any) => o.id}
                                        isClearable
                                        onChange={(e: any) => setSelectedYear(e?.year ?? null)}
                                        value={
                                            selectedYear
                                                ? vesselYears?.find(
                                                    (y: any) => y.year === selectedYear
                                                ) || null
                                                : null
                                        }
                                        isLoading={vesselYearsLoading}
                                        menuPortalTarget={document.body}
                                        styles={customStyle}
                                        isDisabled={!VesselID}
                                    />
                                </Col>
                            </Row>
                        </Card>

                        {/* Require Filters */}
                        {(!VesselID || !selectedYear) && (
                            <Message message="Please select a vessel and year to view uploaded files" type="warning" width="500px" />
                        )}

                        {/* Files Display */}
                        {VesselID && selectedYear && (
                            <Card className='height-65 p-1'>
                                {isLoading && (
                                    <Row className="m-0">
                                        <Col md="2">
                                            <Stack spacing={2}>
                                                <Skeleton variant="rectangular" height={40} />
                                                <Skeleton variant="rectangular" height={40} />
                                                <Skeleton variant="rectangular" height={40} />
                                                <Skeleton variant="rectangular" height={40} />
                                                <Skeleton variant="rectangular" height={40} />
                                                <Skeleton variant="rectangular" height={40} />
                                            </Stack>
                                        </Col>
                                        <Col md="10">
                                            <Skeleton variant="rectangular" height={40} />
                                            <FormLoading />
                                        </Col>
                                    </Row>
                                )}

                                {isError && <ErrorComponent message='Error loading required data!' />}

                                {!(isError || isLoading) && (
                                    <>
                                        <div className="mb-3">
                                            <h5 className="mb-1">{title}</h5>
                                            <p className="text-muted mb-0">{description}</p>
                                        </div>
                                        <CardBody className="p-0">
                                            <Card className="p-0 d-flex flex-row border-0">
                                                <CardHeader className="p-0 border-end rounded-0 custom-card-width">
                                                    <Nav vertical>
                                                        {(hasRole(Roles.ES_ADMIN) ||
                                                            hasRole(Roles.MANAGER) ||
                                                            hasRole(Roles.MASTER) ||
                                                            hasRole(Roles.CHIEFENGINEER)) && (
                                                                <>
                                                                    <div className="px-3 py-2 border-bottom bg-light fw-semibold text-dark d-flex justify-content-between">
                                                                        <span>Type of File</span>
                                                                        <span>No. of Files</span>
                                                                    </div>

                                                                    <NavItem>
                                                                        <NavLink
                                                                            className={classnames("vertical-tab-link d-flex justify-content-between align-items-center", {
                                                                                active: activeAdjustmentsTab === "1",
                                                                            })}
                                                                            onClick={() => {
                                                                                toggle("1")
                                                                                setTitle('BDN Files')
                                                                                setDescription('Uploaded BDN Files')
                                                                            }}
                                                                        >
                                                                            <span>BDN Files</span>
                                                                            <span className="badge rounded-pill blue-badge">
                                                                                {AllFiles?.BDN?.file_count ?? 0}
                                                                            </span>
                                                                        </NavLink>
                                                                    </NavItem>

                                                                    <NavItem>
                                                                        <NavLink
                                                                            className={classnames("vertical-tab-link d-flex justify-content-between align-items-center", {
                                                                                active: activeAdjustmentsTab === "2",
                                                                            })}
                                                                            onClick={() => {
                                                                                toggle("2")
                                                                                setTitle('EDN Files')
                                                                                setDescription('Uploaded EDN Files')
                                                                            }}
                                                                        >
                                                                            <span>EDN Files</span>
                                                                            <span className="badge rounded-pill blue-badge">
                                                                                {AllFiles?.EDN?.file_count ?? 0}
                                                                            </span>
                                                                        </NavLink>
                                                                    </NavItem>

                                                                    {(vesselType === VesselTypeConstant.CONTAINER ||
                                                                        vesselType === VesselTypeConstant.GENERAL_CARGO_SHIP) &&
                                                                        OtherSettings?.[0]?.reefer_container_available && (
                                                                            <NavItem>
                                                                                <NavLink
                                                                                    className={classnames(
                                                                                        "vertical-tab-link d-flex justify-content-between align-items-center",
                                                                                        { active: activeAdjustmentsTab === "3" }
                                                                                    )}
                                                                                    onClick={() => {
                                                                                        toggle("3")
                                                                                        setTitle('Cargo Docs')
                                                                                        setDescription('Uploaded Cargo Documents')
                                                                                    }}
                                                                                >
                                                                                    <span>Cargo Docs</span>
                                                                                    <span className="badge rounded-pill blue-badge">
                                                                                        {AllFiles?.ReeferContainer?.file_count ?? 0}
                                                                                    </span>
                                                                                </NavLink>
                                                                            </NavItem>
                                                                        )}

                                                                    {[
                                                                        VesselTypeConstant.TANKER,
                                                                        VesselTypeConstant.GAS_CARRIER,
                                                                        VesselTypeConstant.LNG_CARRIER,
                                                                    ].includes(vesselType) && (
                                                                            <NavItem>
                                                                                <NavLink
                                                                                    className={classnames(
                                                                                        "vertical-tab-link d-flex justify-content-between align-items-center",
                                                                                        { active: activeAdjustmentsTab === "4" }
                                                                                    )}
                                                                                    onClick={() => {
                                                                                        toggle("4")
                                                                                        setTitle('Log Book Files')
                                                                                        setDescription('Uploaded Log Book Files')
                                                                                    }}
                                                                                >
                                                                                    <span>Log Book Files</span>
                                                                                    <span className="badge rounded-pill blue-badge">
                                                                                        {AllFiles?.SpecialOperationFileUpload?.file_count ?? 0}
                                                                                    </span>
                                                                                </NavLink>
                                                                            </NavItem>
                                                                        )}

                                                                    {vesselType === VesselTypeConstant.LNG_CARRIER && (
                                                                        <NavItem>
                                                                            <NavLink
                                                                                className={classnames("vertical-tab-link d-flex justify-content-between align-items-center", {
                                                                                    active: activeAdjustmentsTab === "5",
                                                                                })}
                                                                                onClick={() => {
                                                                                    toggle("5")
                                                                                    setTitle('N<sub>2</sub> Adjustments Files')
                                                                                    setDescription('Uploaded N<sub>2</sub> Adjustments Files')
                                                                                }}
                                                                            >
                                                                                <span>N<sub>2</sub> Adjustments Files</span>
                                                                                <span className="badge rounded-pill blue-badge">
                                                                                    {AllFiles?.N2Adjustments?.file_count ?? 0}
                                                                                </span>
                                                                            </NavLink>
                                                                        </NavItem>
                                                                    )}

                                                                    <NavItem>
                                                                        <NavLink
                                                                            className={classnames("vertical-tab-link d-flex justify-content-between align-items-center", {
                                                                                active: activeAdjustmentsTab === "6",
                                                                            })}
                                                                            onClick={() => {
                                                                                toggle("6")
                                                                                setTitle('CII Adjustment Files')
                                                                                setDescription('Uploaded CII Adjustment Files')
                                                                            }}
                                                                        >
                                                                            <span>CII Adjustment Files</span>
                                                                            <span className="badge rounded-pill blue-badge">
                                                                                {AllFiles?.CIIAdjustmentSupportingFile?.file_count ?? 0}
                                                                            </span>
                                                                        </NavLink>
                                                                    </NavItem>

                                                                    {(vesselType === VesselTypeConstant.TANKER ||
                                                                        vesselType === VesselTypeConstant.GENERAL_CARGO_SHIP ||
                                                                        vesselType === VesselTypeConstant.BULK_CARRIER) && (
                                                                            <NavItem>
                                                                                <NavLink
                                                                                    className={classnames("vertical-tab-link d-flex justify-content-between align-items-center", {
                                                                                        active: activeAdjustmentsTab === "7",
                                                                                    })}
                                                                                    onClick={() => {
                                                                                        toggle("7")
                                                                                        setTitle('BL Attachment')
                                                                                        setDescription('Uploaded Cargo Documents')
                                                                                    }}
                                                                                >
                                                                                    <span>BL Attachment</span>
                                                                                    <span className="badge rounded-pill blue-badge">
                                                                                        {AllFiles?.BillOfLadingBLFigureFile?.file_count ?? 0}
                                                                                    </span>
                                                                                </NavLink>
                                                                            </NavItem>
                                                                        )}
                                                                </>
                                                            )}
                                                    </Nav>
                                                </CardHeader>

                                                <CardBody className="pt-1 w-100 border-bottom-0">
                                                    <TabContent activeTab={activeAdjustmentsTab} className="text-muted border-bottom-0">
                                                        <TabPane tabId="1">
                                                            <FileTable
                                                                files={AllFiles?.BDN?.files || []}
                                                                tabId={1}
                                                            />
                                                        </TabPane>

                                                        <TabPane tabId="2">
                                                            <FileTable
                                                                files={AllFiles?.EDN?.files || []}
                                                                tabId={2}
                                                            />
                                                        </TabPane>

                                                        <TabPane tabId="3">
                                                            <FileTable
                                                                files={AllFiles?.ReeferContainer?.files || []}
                                                                tabId={3}
                                                            />
                                                        </TabPane>

                                                        <TabPane tabId="4">
                                                            <FileTable
                                                                files={AllFiles?.SpecialOperationFileUpload?.files || []}
                                                                tabId={4}
                                                            />
                                                        </TabPane>

                                                        <TabPane tabId="5">
                                                            <FileTable
                                                                files={AllFiles?.N2Adjustment?.files || []}
                                                                tabId={5}
                                                            />
                                                        </TabPane>

                                                        <TabPane tabId="6">
                                                            <FileTable
                                                                files={AllFiles?.CIIAdjustmentSupportingFile?.files || []}
                                                                tabId={6}
                                                            />
                                                        </TabPane>

                                                        <TabPane tabId="7">
                                                            <FileTable
                                                                files={AllFiles?.BillOfLadingBLFigureFile?.files || []}
                                                                tabId={7}
                                                            />
                                                        </TabPane>
                                                    </TabContent>
                                                </CardBody>
                                            </Card>
                                        </CardBody>
                                    </>
                                )}
                            </Card>
                        )}
                    </Container>
                </div>
            </Layout>
        </React.Fragment>
    )
}

export default AllUploadedFiles